import React from 'react';
import './BFButton.css';

export type BFButtonSize = 'small' | 'normal' | 'large';
export type BFButtonBorderRadius = 'rounded' | 'normal';
export type BFButtonColor = 'primary' | 'accent';

export interface BFButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
    /** HTML button type — use "submit" to trigger form submission on Enter */
    type?: 'button' | 'submit' | 'reset';
    /** Button size */
    size?: BFButtonSize;
    /** Border radius style */
    borderRadius?: BFButtonBorderRadius;
    /** Button color variant */
    color?: BFButtonColor;
    /** Whether the button is disabled */
    disabled?: boolean;
    /** Icon to display before text */
    iconPrefix?: React.ReactNode;
    /** Icon to display after text */
    iconSuffix?: React.ReactNode;
    /** Button children content */
    children?: React.ReactNode;
    /** Loading state */
    loading?: boolean;
}

const BFButton = React.forwardRef<HTMLButtonElement, BFButtonProps>(
    (
        {
            type = 'button',
            size = 'normal',
            borderRadius = 'normal',
            color = 'primary',
            disabled = false,
            iconPrefix,
            iconSuffix,
            children,
            loading = false,
            className = '',
            ...props
        },
        ref,
    ) => {
        const classes = [
            'bf-button',
            `bf-button--${size}`,
            `bf-button--${borderRadius}`,
            `bf-button--${color}`,
            disabled ? 'bf-button--disabled' : '',
            loading ? 'bf-button--loading' : '',
            className,
        ]
            .filter(Boolean)
            .join(' ');

        return (
            <button
                ref={ref}
                className={classes}
                // eslint-disable-next-line react/button-has-type
                type={type}
                disabled={disabled || loading}
                {...props}
            >
                {loading && <span className="bf-button__spinner" />}
                {!loading && iconPrefix && (
                    <span className="bf-button__icon bf-button__icon--prefix">{iconPrefix}</span>
                )}
                {children && <span className="bf-button__content">{children}</span>}
                {!loading && iconSuffix && (
                    <span className="bf-button__icon bf-button__icon--suffix">{iconSuffix}</span>
                )}
            </button>
        );
    },
);

BFButton.displayName = 'BFButton';
export default BFButton;
